// src/pos-app/stations/WaiterSalesStation.js
//
// F15 part 2 — the Waiter Sales station, the fourth in the native app.
//
// The web hub offers Cashier Sales and Table Service as separate stations.
// The native app had three — Cashier, "Waiter", KDS — where "Waiter" mounts
// TablesSurface, i.e. Table Service (floor plan, waitlist, reservations)
// under the wrong name; WaiterStation.js:9-15 documents that mismatch itself,
// and the enrolment tile even promised "Take orders at the table" for a
// surface that does no order taking. This station is the one that actually
// does, and it is phone-first: a waiter walking a floor with a phone is the
// single strongest phone use case in the product, and it had no home.
//
// Table Service stays tablet. This station is the phone one. Both remain
// reachable — see station/stationConfig.js, where TABLE_SERVICE deliberately
// keeps the STORED value 'waiter' so devices already enrolled in the field
// keep booting into the surface they always had.
//
// Composition mirrors CashierStation/WaiterStation deliberately rather than
// abstracting a shared shell: those two already diverge (SellExtras bridge,
// legacy navigation guards, sales-mode switch) and a premature extraction
// would have to be re-threaded through the one file this task's spec calls
// out as the highest regression risk. Mirrored, with this comment as the
// drift warning.
//
// Tabs are Tables / Orders / Shift — three SHORT labels. That is not
// incidental: five tabs with a label as long as "Clock In / Out" is exactly
// what truncated on a phone (see BottomTabs.js), so this station is built to
// stay clear of that ceiling rather than rely on the step-down rule.
//
// Orientation: this station does NOT lock it. See the effect below — copying
// WaiterStation's portrait lock measurably reintroduced the Dynamic Island
// overlap this branch had just fixed.
import React, { useEffect, useRef, useState } from 'react';
import AuroraBackground, { glass } from '../shell/AuroraBackground';
import StationTopBar from '../shell/StationTopBar';
import BottomTabs from '../shell/BottomTabs';
import useOperatorSession from '../shell/useOperatorSession';
import useLockSessionStatus from '../../hooks/pos/useLockSessionStatus';
import useBusinessDayGate from '../shell/useBusinessDayGate';
import DayClosedScreen from '../shell/DayClosedScreen';
import StationSettingsSheet from '../StationSettingsSheet';
import OperatorLockScreen from '../OperatorLockScreen';
import { ConnectionBanner } from '../ConnectionStatus';
import WaiterSalesSurface from '../surfaces/waiter-sales/WaiterSalesSurface';
import { setOrientation } from '../../platform/nativeChrome';
import { useLanguage } from '../../context/LanguageContext';
import { useStation } from '../../station/StationContext';

const CSS = `
/* Frame padding moved off the FRAME and onto the body (F15 part 2). The top
   bar and the bottom tab bar are chrome: they must reach the display edges
   like native bars, with content passing under them. Only the content column
   between them is inset. */
.wsst-frame{flex:1;min-height:0;display:flex;flex-direction:column;box-sizing:border-box;}
.wsst-body{flex:1;min-height:0;display:flex;padding:10px 12px 0;box-sizing:border-box;}
.wsst-pane{flex:1;min-width:0;min-height:0;display:flex;flex-direction:column;}
.wsst-surface{flex:1;min-height:0;position:relative;overflow:hidden;}
.wsst-surface-inner{position:absolute;inset:0;display:flex;flex-direction:column;overflow:hidden;}

.wsst-shift{flex:1;min-height:0;overflow-y:auto;display:flex;align-items:center;justify-content:center;
  padding:24px;box-sizing:border-box;}
.wsst-op-card{width:360px;max-width:100%;box-sizing:border-box;border-radius:20px;padding:26px 24px 22px;
  display:flex;flex-direction:column;align-items:center;gap:6px;text-align:center;}
.wsst-op-avatar{width:68px;height:68px;border-radius:22px;margin-bottom:8px;flex:none;
  display:flex;align-items:center;justify-content:center;color:#fff;font-size:22px;font-weight:800;
  background:linear-gradient(135deg,var(--c-green),var(--c-teal));}
.wsst-op-name{font-size:18px;font-weight:800;letter-spacing:-.2px;color:var(--c-ink);}
.wsst-op-role{font-size:11.5px;font-weight:800;text-transform:uppercase;letter-spacing:.8px;
  color:var(--c-muted);}
.wsst-op-since{font-size:12.5px;color:var(--c-muted);font-weight:600;
  display:flex;align-items:center;gap:6px;}
.wsst-end{margin-top:18px;width:100%;min-height:56px;border:none;border-radius:14px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;gap:9px;
  background:var(--c-green);color:#fff;font-family:inherit;font-size:15px;font-weight:800;
  transition:transform .12s, filter .12s;}
.wsst-end:active{transform:scale(.96);filter:brightness(1.1);}
[dir="rtl"] .wsst-end svg{transform:scaleX(-1);}
`;

const ClockIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
    </svg>
);

const EndShiftIcon = (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
    </svg>
);

const initials = (name) => (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

export default function WaiterSalesStation() {
    const { t } = useLanguage();
    const { config } = useStation();

    const { operator, locked, unlocking, error, lock, unlock } = useOperatorSession(config?.tenantId);
    const sessionStatus = useLockSessionStatus(locked);
    const { dayOpen } = useBusinessDayGate();

    const [activeTab, setActiveTab] = useState('tables');
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [shiftStartedAt, setShiftStartedAt] = useState(null);

    // The surface mounts on first unlock and then STAYS mounted across
    // mid-shift locks, so a half-entered round survives a handover — same
    // everUnlockedRef pattern both sibling stations use.
    const everUnlockedRef = useRef(false);
    if (operator != null) everUnlockedRef.current = true;
    const everUnlocked = everUnlockedRef.current;

    // ---- orientation ------------------------------------------------------
    // This station does NOT lock orientation, and that is deliberate.
    //
    // WaiterStation locks phones to portrait. Copying that here reproduced a
    // clear regression on an iPhone 17 Pro Max simulator: the station top bar
    // rendered under the Dynamic Island again, on a build where the cashier
    // station — which does not lock — sat correctly below it. Applying a
    // native orientation lock re-lays out the WKWebView and the content inset
    // that capacitor.config.ts's ios.contentInset='always' relies on does not
    // survive it.
    //
    // CashierStation reached the same conclusion from the other direction:
    // f8548be ("Allow cashier portrait orientation", 2026-07-18) removed its
    // lock outright because a native lock on iPadOS 26 could force the app
    // into a floating/windowed state where hit-testing desyncs from what is
    // painted. Two independent native-layout failures from the same call is
    // enough — this station follows cashier's precedent.
    //
    // The device still behaves sensibly: iOS honours the app's supported
    // orientations, and a waiter holding a phone is holding it portrait
    // anyway. What is NOT acceptable is chrome under the Island on every
    // screen, which is what the lock costs.
    //
    // Still sweep any STALE lock on mount — a device previously enrolled as
    // KDS (which locks landscape) or Table Service (which locks portrait)
    // carries that lock into this station otherwise, and merely not setting a
    // new one never clears one already applied. Same defensive sweep
    // CashierStation performs unconditionally.
    useEffect(() => {
        setOrientation(null);
    }, []);

    const handleUnlock = async (pin) => {
        const ok = await unlock(pin);
        if (ok) setShiftStartedAt(new Date());
        return ok;
    };

    const tabs = [
        { key: 'tables', label: t('tables'), icon: 'table' },
        { key: 'orders', label: t('orders'), icon: 'receipt' },
        { key: 'shift', label: t('shift'), icon: 'clock' },
    ];

    const shiftSince = shiftStartedAt
        ? shiftStartedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : null;

    return (
        <AuroraBackground intensity="calm">
            <style>{CSS}</style>

            {everUnlocked ? (
                <div className="wsst-frame">
                    <StationTopBar
                        stationLabel={t('waiter_sales')}
                        operator={operator || undefined}
                        onLock={lock}
                        onSettings={() => setSettingsOpen(true)}
                        compact
                    />
                    <ConnectionBanner />

                    <main className="wsst-body">
                        {/* Tables / Orders share ONE always-mounted pane, display-
                            toggled so an in-progress cart survives a tab switch —
                            the same trick CashierStation uses for Sell/Orders. The
                            surface itself swaps its content off `ordersTabActive`. */}
                        <section
                            className="wsst-pane"
                            role="tabpanel"
                            aria-label={activeTab === 'orders' ? t('orders') : t('tables')}
                            style={{
                                display: (activeTab === 'tables' || activeTab === 'orders') ? 'flex' : 'none',
                            }}
                        >
                            <div className="wsst-surface" style={glass({ borderRadius: 16 })}>
                                {/* Business-day gate stays ABOVE the surface: while
                                    the day is closed it never mounts, so nothing
                                    inside can try to write an order against no
                                    sales date. Shift stays reachable regardless so
                                    an operator can still hand over or lock out. */}
                                {dayOpen ? (
                                    <div className="wsst-surface-inner">
                                        <WaiterSalesSurface
                                            ordersTabActive={activeTab === 'orders'}
                                            onRequestSellTab={() => setActiveTab('tables')}
                                        />
                                    </div>
                                ) : (
                                    <div className="wsst-surface-inner">
                                        <DayClosedScreen />
                                    </div>
                                )}
                            </div>
                        </section>

                        {activeTab === 'shift' && (
                            <section className="wsst-pane" role="tabpanel" aria-label={t('shift')}>
                                <div className="wsst-shift">
                                    <div className="wsst-op-card" style={glass({ borderRadius: 20 })}>
                                        <div className="wsst-op-avatar" aria-hidden="true">{initials(operator?.name)}</div>
                                        <div className="wsst-op-name">{operator?.name}</div>
                                        {operator?.role ? <div className="wsst-op-role">{t(operator.role)}</div> : null}
                                        {shiftSince ? (
                                            <div className="wsst-op-since">
                                                {ClockIcon}
                                                {t('on_shift_since')} {shiftSince}
                                            </div>
                                        ) : null}
                                        <button type="button" className="wsst-end" onClick={lock}>
                                            {EndShiftIcon}
                                            {t('end_shift')}
                                        </button>
                                    </div>
                                </div>
                            </section>
                        )}
                    </main>

                    <BottomTabs tabs={tabs} activeKey={activeTab} onChange={setActiveTab} />
                </div>
            ) : null}

            {locked && (
                <OperatorLockScreen
                    stationLabel={t('waiter_sales')}
                    unlocking={unlocking}
                    error={error}
                    onSubmit={handleUnlock}
                    sessionStatus={sessionStatus}
                />
            )}

            <StationSettingsSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} />
        </AuroraBackground>
    );
}
